import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Trash2, MoveUp, MoveDown, Image, Type, Layout, Link2,
  Bold, Italic, List, AlignLeft, AlignCenter, AlignRight, Eye, Code,
  Columns, Facebook, Instagram, Linkedin, Twitter, Mail, ArrowRight,
  Settings2, ChevronDown
} from 'lucide-react';
import apiClient from '../utils/api';
import { substituteText } from '../utils/propertySubstitution';
import { useIsMobile } from '../hooks/useIsMobile';
import { RewriteWithAI } from './RewriteWithAI';

// Available fonts for newsletter
const NEWSLETTER_FONTS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
];

// Block types
const BLOCK_TYPES = [
  { id: 'header', name: 'Header', icon: Layout, description: 'Logo and title section' },
  { id: 'text', name: 'Text Block', icon: Type, description: 'Rich text content' },
  { id: 'image', name: 'Image', icon: Image, description: 'Single image with caption' },
  { id: 'twoColumn', name: 'Two Columns', icon: Columns, description: 'Image + Text side by side' },
  { id: 'button', name: 'Button', icon: Link2, description: 'Call-to-action button' },
  { id: 'html', name: 'HTML Code', icon: Code, description: 'Raw HTML snippet' },
  { id: 'socialIcons', name: 'Social Icons', icon: Facebook, description: 'Social media links' },
  { id: 'divider', name: 'Divider', icon: Layout, description: 'Horizontal separator' },
  { id: 'spacer', name: 'Spacer', icon: Layout, description: 'Vertical space' },
  { id: 'footer', name: 'Footer', icon: Mail, description: 'Signature & Unsubscribe' },
];

// Create a new block
const createBlock = (type) => {
  const baseBlock = {
    id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
  };

  switch (type) {
    case 'header':
      return {
        ...baseBlock,
        logoUrl: '',
        title: 'Your Newsletter Title',
        subtitle: 'Your tagline here',
        backgroundColor: '#064E3B',
        textColor: '#FFFFFF',
        padding: 40,
      };
    case 'text':
      return {
        ...baseBlock,
        content: '<p>Start writing your content here...</p>',
        fontFamily: 'Arial, sans-serif',
        fontSize: 16,
        textColor: '#333333',
        backgroundColor: '#FFFFFF',
        padding: 20,
        textAlign: 'left',
      };
    case 'image':
      return {
        ...baseBlock,
        imageUrl: '',
        altText: 'Image description',
        caption: '',
        width: '100%',
        alignment: 'center',
        padding: 20,
      };
    case 'twoColumn':
      return {
        ...baseBlock,
        leftContent: 'image', // 'image' or 'text'
        rightContent: 'text',
        imageUrl: '',
        imageAlt: 'Image',
        textContent: '<p>Your text content here...</p>',
        backgroundColor: '#FFFFFF',
        padding: 20,
        imageWidth: '50%',
      };
    case 'button':
      return {
        ...baseBlock,
        text: 'Click Here',
        url: 'https://',
        backgroundColor: '#064E3B',
        textColor: '#FFFFFF',
        borderRadius: 4,
        padding: 20,
        alignment: 'center',
      };
    case 'socialIcons':
      return {
        ...baseBlock,
        facebook: '',
        instagram: '',
        linkedin: '',
        twitter: '',
        iconSize: 32,
        iconColor: '#333333',
        backgroundColor: '#FFFFFF',
        padding: 20,
        alignment: 'center',
      };
    case 'divider':
      return {
        ...baseBlock,
        color: '#E5E5E5',
        thickness: 1,
        width: '100%',
        padding: 20,
      };
    case 'spacer':
      return {
        ...baseBlock,
        height: 30,
      };
    case 'footer':
      return {
        ...baseBlock,
        companyName: '',
        address: '',
        phone: '',
        email: '',
        showSignature: true,
        signatureName: '',
        signatureTitle: '',
        showUnsubscribe: true,
        unsubscribeText: 'Unsubscribe from this list',
        backgroundColor: '#F5F5F5',
        textColor: '#666666',
        padding: 30,
      };
    case 'html':
      return {
        ...baseBlock,
        code: '<div style="padding:20px;text-align:center;">\n  <p>Your custom HTML here</p>\n</div>',
        padding: 0,
      };
    default:
      return baseBlock;
  }
};

// Block Preview Components
const BlockPreview = ({ block, onUploadPlaceholderClick, isHeroSlot = false }) => {
  switch (block.type) {
    case 'header':
      return (
        <div style={{ backgroundColor: block.backgroundColor, color: block.textColor, padding: `${block.padding}px`, textAlign: 'center' }}>
          {block.logoUrl && <img src={block.logoUrl} alt="Logo" style={{ maxHeight: '60px', marginBottom: '16px' }} />}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>{block.title}</h1>
          {block.subtitle && <p style={{ fontSize: '16px', opacity: 0.9, margin: 0 }}>{block.subtitle}</p>}
        </div>
      );
    case 'text':
      return (
        <div style={{ backgroundColor: block.backgroundColor, padding: `${block.padding}px`, fontFamily: block.fontFamily, fontSize: `${block.fontSize}px`, color: block.textColor, textAlign: block.textAlign }} dangerouslySetInnerHTML={{ __html: block.content }} />
      );
    case 'image':
      return (
        <div style={{ padding: `${block.padding}px`, textAlign: block.alignment }}>
          {block.imageUrl ? (
            <img src={block.imageUrl} alt={block.altText} style={{ width: block.width, maxWidth: '100%' }} />
          ) : (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onUploadPlaceholderClick && onUploadPlaceholderClick(block.id);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onUploadPlaceholderClick && onUploadPlaceholderClick(block.id);
                }
              }}
              data-testid={`newsletter-image-placeholder-${block.id}`}
              style={{ background: '#F3F4F6', height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', border: '2px dashed #D1D5DB', borderRadius: '8px', margin: block.padding ? '0' : '0', cursor: 'pointer' }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '24px', color: '#6B7280', lineHeight: 1 }}>+</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500' }}>{isHeroSlot ? 'Tap to add hero photo' : 'Tap to upload image'}</span>
            </div>
          )}
          {block.caption && <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>{block.caption}</p>}
        </div>
      );
    case 'twoColumn':
      const leftIsImage = block.leftContent === 'image';
      const twoColPlaceholder = (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onUploadPlaceholderClick && onUploadPlaceholderClick(block.id);
          }}
          role="button"
          tabIndex={0}
          data-testid={`newsletter-image-placeholder-${block.id}`}
          style={{ background: '#E5E5E5', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', cursor: 'pointer' }}
        >
          + Tap to upload
        </div>
      );
      return (
        <div style={{ backgroundColor: block.backgroundColor, padding: `${block.padding}px` }}>
          <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td width="50%" style={{ verticalAlign: 'top', padding: '10px' }}>
                  {leftIsImage ? (
                    block.imageUrl ? <img src={block.imageUrl} alt={block.imageAlt} style={{ width: '100%' }} /> : twoColPlaceholder
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: block.textContent }} />
                  )}
                </td>
                <td width="50%" style={{ verticalAlign: 'top', padding: '10px' }}>
                  {!leftIsImage ? (
                    block.imageUrl ? <img src={block.imageUrl} alt={block.imageAlt} style={{ width: '100%' }} /> : twoColPlaceholder
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: block.textContent }} />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    case 'button':
      return (
        <div style={{ padding: `${block.padding}px`, textAlign: block.alignment }}>
          <a href={block.url} style={{ display: 'inline-block', backgroundColor: block.backgroundColor, color: block.textColor, padding: '12px 24px', borderRadius: `${block.borderRadius}px`, textDecoration: 'none', fontWeight: 'bold' }}>{block.text}</a>
        </div>
      );
    case 'socialIcons':
      return (
        <div style={{ backgroundColor: block.backgroundColor, padding: `${block.padding}px`, textAlign: block.alignment }}>
          <div style={{ display: 'inline-flex', gap: '16px' }}>
            {block.facebook && <a href={block.facebook} style={{ color: block.iconColor }}><Facebook size={block.iconSize} /></a>}
            {block.instagram && <a href={block.instagram} style={{ color: block.iconColor }}><Instagram size={block.iconSize} /></a>}
            {block.linkedin && <a href={block.linkedin} style={{ color: block.iconColor }}><Linkedin size={block.iconSize} /></a>}
            {block.twitter && <a href={block.twitter} style={{ color: block.iconColor }}><Twitter size={block.iconSize} /></a>}
            {!block.facebook && !block.instagram && !block.linkedin && !block.twitter && <span style={{ color: '#999' }}>Add social links in settings</span>}
          </div>
        </div>
      );
    case 'divider':
      return (
        <div style={{ padding: `${block.padding}px 20px` }}>
          <hr style={{ border: 'none', borderTop: `${block.thickness}px solid ${block.color}`, width: block.width, margin: '0 auto' }} />
        </div>
      );
    case 'spacer':
      return <div style={{ height: `${block.height}px` }} />;
    case 'html':
      return (
        <div style={{ padding: `${block.padding || 0}px`, position: 'relative' }}>
          <div dangerouslySetInnerHTML={{ __html: block.code }} />
          <div style={{ position: 'absolute', top: 4, right: 4, background: '#1a1a2e', color: '#fff', fontSize: '9px', padding: '2px 6px', borderRadius: '3px', opacity: 0.7 }}>HTML</div>
        </div>
      );
    case 'footer':
      return (
        <div style={{ backgroundColor: block.backgroundColor, color: block.textColor, padding: `${block.padding}px`, textAlign: 'center', fontSize: '14px' }}>
          {block.showSignature && (block.signatureName || block.signatureTitle) && (
            <div style={{ marginBottom: '20px' }}>
              {block.signatureName && <p style={{ fontWeight: 'bold', margin: '0 0 4px 0' }}>{block.signatureName}</p>}
              {block.signatureTitle && <p style={{ margin: 0, opacity: 0.8 }}>{block.signatureTitle}</p>}
            </div>
          )}
          {block.companyName && <p style={{ fontWeight: 'bold', margin: '0 0 4px 0' }}>{block.companyName}</p>}
          {block.address && <p style={{ margin: '0 0 4px 0' }}>{block.address}</p>}
          {block.phone && <p style={{ margin: '0 0 4px 0' }}>{block.phone}</p>}
          {block.email && <p style={{ margin: '0 0 16px 0' }}>{block.email}</p>}
          {block.showUnsubscribe && (
            <p style={{ marginTop: '16px', borderTop: '1px solid #ddd', paddingTop: '16px' }}>
              <a href="#unsubscribe" style={{ color: block.textColor, textDecoration: 'underline' }}>{block.unsubscribeText}</a>
            </p>
          )}
        </div>
      );
    default:
      return null;
  }
};

export const NewsletterBuilder = ({ template, onSave, initialBlocks, availablePhotos = [], propertyData = null }) => {
  const isMobile = useIsMobile(900);
  // Bottom-sheet state on mobile. `null` = no sheet, `'add'` = block-type picker,
  // `'settings'` = block settings panel for the currently-selected block.
  const [mobileSheet, setMobileSheet] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const fileInputRef = useRef();

  // ── Undo / Redo history ───────────────────────────────────────────────
  // Snapshot-based history of `blocks`. We capture the prior state into
  // `pastRef` on every meaningful change (via debounced effect) and clear
  // `futureRef` whenever a new edit happens.
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const suppressHistoryRef = useRef(false);
  const lastSnapshotRef = useRef(null);
  useEffect(() => {
    // Don't record history for undo/redo-triggered state changes themselves.
    if (suppressHistoryRef.current) {
      suppressHistoryRef.current = false;
      lastSnapshotRef.current = JSON.stringify(blocks);
      return;
    }
    const t = setTimeout(() => {
      const snap = JSON.stringify(blocks);
      if (lastSnapshotRef.current !== null && lastSnapshotRef.current !== snap) {
        pastRef.current = [...pastRef.current.slice(-49), JSON.parse(lastSnapshotRef.current)];
        futureRef.current = [];
      }
      lastSnapshotRef.current = snap;
    }, 400);
    return () => clearTimeout(t);
  }, [blocks]);

  const undoBlocks = useCallback(() => {
    if (pastRef.current.length === 0) return;
    futureRef.current = [JSON.parse(JSON.stringify(blocks)), ...futureRef.current.slice(0, 49)];
    const restored = pastRef.current.pop();
    suppressHistoryRef.current = true;
    setBlocks(restored);
  }, [blocks]);

  const redoBlocks = useCallback(() => {
    if (futureRef.current.length === 0) return;
    pastRef.current = [...pastRef.current, JSON.parse(JSON.stringify(blocks))];
    const restored = futureRef.current.shift();
    suppressHistoryRef.current = true;
    setBlocks(restored);
  }, [blocks]);

  useEffect(() => {
    // Initialize with template blocks, initialBlocks prop, or defaults
    if (initialBlocks && initialBlocks.length > 0) {
      setBlocks(initialBlocks);
    } else if (template?.canvas_data?.blocks) {
      setBlocks(template.canvas_data.blocks);
    } else {
      setBlocks([
        createBlock('header'),
        createBlock('text'),
        createBlock('footer'),
      ]);
    }
    // Fetch business info for auto-fill
    fetchBusinessInfo();
  }, [template]);

  // Auto-fill empty image / twoColumn blocks with property photos.
  // We rely on the `!b.imageUrl` check to never overwrite blocks the user has
  // already populated — no separate per-template gate is needed and an earlier
  // gate caused a race where the effect bailed before `blocks` had hydrated
  // from `initialBlocks` and then locked itself permanently.
  useEffect(() => {
    if (!template?.template_id) return;
    if (!availablePhotos || availablePhotos.length === 0) return;
    if (blocks.length === 0) return;

    let cursor = 0;
    let mutated = false;
    const next = blocks.map(b => {
      if ((b.type === 'image' || b.type === 'twoColumn') && !b.imageUrl) {
        if (cursor < availablePhotos.length) {
          mutated = true;
          return { ...b, imageUrl: availablePhotos[cursor++], autoFilled: true };
        }
      } else if ((b.type === 'image' || b.type === 'twoColumn') && b.imageUrl) {
        // Block already filled — advance the cursor so the next empty block
        // gets the SECOND photo, not a duplicate of the first.
        cursor++;
      }
      return b;
    });
    if (mutated) setBlocks(next);
  }, [template?.template_id, availablePhotos, blocks]);

  // Substitute property placeholders ({{address}}, {{price}}, {{beds}}, etc.)
  // in newsletter block text fields. Case-insensitive so {{ADDRESS}},
  // {{Address}}, {{address}} all match. We rely on the substituteText helper
  // to be a no-op when the text contains no `{{` tokens (so once a block has
  // been substituted, subsequent re-runs return the same string by reference
  // and React won't render again). Removing the per-template gate fixes a
  // race where blocks hadn't hydrated yet on first render.
  useEffect(() => {
    if (!template?.template_id) return;
    if (!propertyData || Object.keys(propertyData).length === 0) return;
    if (blocks.length === 0) return;

    const sub = (v) => substituteText(v, propertyData);
    let mutated = false;
    const next = blocks.map(b => {
      const updated = { ...b };
      const textFields = [
        'title', 'subtitle', 'content', 'text', 'caption',
        'textContent', 'imageAlt', 'altText', 'url',
        'signatureName', 'signatureTitle', 'signatureContact',
        'companyName', 'email', 'phone', 'address',
        'preheader', 'footer',
      ];
      for (const f of textFields) {
        if (typeof updated[f] === 'string' && updated[f].includes('{{')) {
          const replaced = sub(updated[f]);
          if (replaced !== updated[f]) {
            updated[f] = replaced;
            mutated = true;
          }
        }
      }
      return updated;
    });
    if (mutated) setBlocks(next);
  }, [template?.template_id, propertyData, blocks]);

  const fetchBusinessInfo = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setBusinessInfo({
        businessName: response.data.business_name || '',
        businessEmail: response.data.business_email || '',
        businessPhone: response.data.business_phone || '',
        socialLinks: response.data.social_links || {}
      });
    } catch (error) {
      console.error('Failed to fetch business info');
    }
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  const addBlock = (type) => {
    const newBlock = createBlock(type);
    // Auto-fill social icons from business info
    if (type === 'socialIcons' && businessInfo?.socialLinks) {
      newBlock.facebook = businessInfo.socialLinks.facebook || '';
      newBlock.instagram = businessInfo.socialLinks.instagram || '';
      newBlock.linkedin = businessInfo.socialLinks.linkedin || '';
      newBlock.twitter = businessInfo.socialLinks.twitter || '';
    }
    // Auto-fill footer from business info
    if (type === 'footer' && businessInfo) {
      newBlock.companyName = businessInfo.businessName || '';
      newBlock.email = businessInfo.businessEmail || '';
      newBlock.phone = businessInfo.businessPhone || '';
    }
    setBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  const updateBlock = (id, updates) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, ...updates } : b));
  };

  const deleteBlock = (id) => {
    setBlocks(blocks.filter(b => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
  };

  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y
  // (redo), Delete/Backspace (remove selected block). Skip while typing.
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undoBlocks();
        return;
      }
      if ((isMod && e.shiftKey && e.key.toLowerCase() === 'z') || (isMod && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        redoBlocks();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBlockId) {
        e.preventDefault();
        deleteBlock(selectedBlockId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockId, undoBlocks, redoBlocks, blocks]);

  const moveBlock = (id, direction) => {
    const index = blocks.findIndex(b => b.id === id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === blocks.length - 1)) return;
    const newBlocks = [...blocks];
    const [removed] = newBlocks.splice(index, 1);
    newBlocks.splice(direction === 'up' ? index - 1 : index + 1, 0, removed);
    setBlocks(newBlocks);
  };

  const handleImageUpload = (blockId, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        updateBlock(blockId, { imageUrl: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Hidden file input used by the in-canvas tap-to-upload pattern. The
  // active block id is stashed in a ref so the change handler knows which
  // block to populate without needing a closure-bound block argument.
  const placeholderFileInputRef = useRef(null);
  const pendingPlaceholderBlockId = useRef(null);
  const openImageUploaderFor = (blockId) => {
    pendingPlaceholderBlockId.current = blockId;
    setSelectedBlockId(blockId);
    if (placeholderFileInputRef.current) {
      placeholderFileInputRef.current.value = '';
      placeholderFileInputRef.current.click();
    }
  };
  const onPlaceholderFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    const blockId = pendingPlaceholderBlockId.current;
    if (!file || !blockId) return;
    const reader = new FileReader();
    reader.onload = (event) => updateBlock(blockId, { imageUrl: event.target.result });
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    const html = generateHTML();
    onSave?.({ blocks, html });
  };

  const generateHTML = () => {
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:0;font-family:Arial,sans-serif;-webkit-font-smoothing:antialiased;}table{border-spacing:0;border-collapse:collapse;}img{border:0;display:block;}a{color:inherit;}</style></head><body style="margin:0;padding:0;background:#f4f4f4;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;"><tr><td align="center" style="padding:20px 0;"><table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;">`;

    blocks.forEach(block => {
      html += `<tr><td>`;

      switch (block.type) {
        case 'header':
          html += `<div style="background-color:${block.backgroundColor};color:${block.textColor};padding:${block.padding}px;text-align:center;">`;
          if (block.logoUrl) html += `<img src="${block.logoUrl}" alt="Logo" style="max-height:60px;margin:0 auto 16px;display:block;" />`;
          html += `<h1 style="font-size:28px;font-weight:bold;margin:0 0 8px;font-family:Georgia,serif;">${block.title}</h1>`;
          if (block.subtitle) html += `<p style="font-size:16px;opacity:0.9;margin:0;">${block.subtitle}</p>`;
          html += `</div>`;
          break;

        case 'text':
          html += `<div style="background-color:${block.backgroundColor || '#ffffff'};padding:${block.padding}px;font-family:${block.fontFamily || 'Arial,sans-serif'};font-size:${block.fontSize}px;color:${block.textColor};text-align:${block.textAlign || 'left'};line-height:1.6;">${block.content}</div>`;
          break;

        case 'image':
          html += `<div style="padding:${block.padding}px;text-align:${block.alignment || 'center'};">`;
          if (block.imageUrl) {
            html += `<img src="${block.imageUrl}" alt="${block.altText || ''}" style="width:${block.width || '100%'};max-width:100%;display:block;margin:0 auto;" />`;
          } else {
            html += `<div style="background:#e5e5e5;height:200px;display:flex;align-items:center;justify-content:center;color:#999;font-size:14px;">Image Placeholder</div>`;
          }
          if (block.caption) html += `<p style="font-size:14px;color:#666;margin-top:8px;text-align:center;">${block.caption}</p>`;
          html += `</div>`;
          break;

        case 'twoColumn': {
          const imgSide = `<td width="50%" valign="top" style="padding:${block.padding}px;">${block.imageUrl ? `<img src="${block.imageUrl}" alt="${block.imageAlt || ''}" style="width:100%;display:block;" />` : `<div style="background:#e5e5e5;height:150px;"></div>`}</td>`;
          const textSide = `<td width="50%" valign="top" style="padding:${block.padding}px;font-size:14px;color:#333;line-height:1.6;">${block.textContent}</td>`;
          html += `<table width="100%" cellpadding="0" cellspacing="0" style="background:${block.backgroundColor || '#ffffff'};"><tr>${block.leftContent === 'image' ? imgSide + textSide : textSide + imgSide}</tr></table>`;
          break;
        }

        case 'button':
          html += `<div style="padding:${block.padding}px;text-align:${block.alignment || 'center'};">`;
          html += `<a href="${block.url}" style="display:inline-block;background-color:${block.backgroundColor};color:${block.textColor};padding:12px 32px;border-radius:${block.borderRadius || 4}px;text-decoration:none;font-weight:bold;font-size:16px;">${block.text}</a>`;
          html += `</div>`;
          break;

        case 'html':
          html += block.code || '';
          break;

        case 'socialIcons': {
          html += `<div style="background:${block.backgroundColor || '#ffffff'};padding:${block.padding}px;text-align:${block.alignment || 'center'};">`;
          const icons = [];
          if (block.facebook) icons.push(`<a href="${block.facebook}" style="display:inline-block;margin:0 6px;color:${block.iconColor};text-decoration:none;font-size:${block.iconSize || 14}px;">Facebook</a>`);
          if (block.instagram) icons.push(`<a href="${block.instagram}" style="display:inline-block;margin:0 6px;color:${block.iconColor};text-decoration:none;font-size:${block.iconSize || 14}px;">Instagram</a>`);
          if (block.linkedin) icons.push(`<a href="${block.linkedin}" style="display:inline-block;margin:0 6px;color:${block.iconColor};text-decoration:none;font-size:${block.iconSize || 14}px;">LinkedIn</a>`);
          if (block.twitter) icons.push(`<a href="${block.twitter}" style="display:inline-block;margin:0 6px;color:${block.iconColor};text-decoration:none;font-size:${block.iconSize || 14}px;">Twitter</a>`);
          html += icons.join(' &nbsp;|&nbsp; ');
          html += `</div>`;
          break;
        }

        case 'divider':
          html += `<div style="padding:${block.padding}px;"><hr style="border:none;border-top:${block.thickness}px solid ${block.color};width:${block.width};margin:0 auto;" /></div>`;
          break;

        case 'spacer':
          html += `<div style="height:${block.height}px;"></div>`;
          break;

        case 'footer':
          html += `<div style="background:${block.backgroundColor};color:${block.textColor};padding:${block.padding}px;text-align:center;font-size:14px;">`;
          if (block.showSignature && (block.signatureName || block.signatureTitle)) {
            html += `<div style="margin-bottom:20px;">`;
            if (block.signatureName) html += `<p style="font-weight:bold;margin:0 0 4px;">${block.signatureName}</p>`;
            if (block.signatureTitle) html += `<p style="margin:0;opacity:0.8;">${block.signatureTitle}</p>`;
            html += `</div>`;
          }
          if (block.companyName) html += `<p style="font-weight:bold;margin:0 0 4px;">${block.companyName}</p>`;
          if (block.address) html += `<p style="margin:0 0 4px;">${block.address}</p>`;
          if (block.phone) html += `<p style="margin:0 0 4px;">${block.phone}</p>`;
          if (block.email) html += `<p style="margin:0 0 16px;">${block.email}</p>`;
          if (block.showUnsubscribe) {
            html += `<p style="margin-top:16px;border-top:1px solid #ddd;padding-top:16px;"><a href="{{unsubscribe_url}}" style="color:${block.textColor};text-decoration:underline;">${block.unsubscribeText}</a></p>`;
          }
          html += `</div>`;
          break;

        default:
          break;
      }

      html += `</td></tr>`;
    });

    html += `</table></td></tr></table></body></html>`;
    return html;
  };

  return (
    <div className={isMobile ? 'flex flex-col h-full bg-muted/10' : 'flex h-full'} data-testid="newsletter-builder">
      {/* Hidden file input used by tap-to-upload on image placeholders */}
      <input
        ref={placeholderFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onPlaceholderFileChange}
      />

      {/* Mobile top bar — minimal, just Preview + Save (Canva-style) */}
      {isMobile && (
        <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-border/50 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => setShowPreview(!showPreview)} data-testid="newsletter-mobile-preview">
            <Eye className="h-4 w-4 mr-1" /> {showPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button size="sm" onClick={handleSave} data-testid="newsletter-mobile-save">
            <ArrowRight className="h-4 w-4 mr-1" /> Save
          </Button>
        </div>
      )}

      {/* Left Panel - Block Types — desktop only */}
      <div className={`${isMobile ? 'hidden' : 'w-64'} border-r border-border/50 bg-card p-4 overflow-y-auto`}>
        <h3 className="font-semibold mb-4">Add Blocks</h3>
        <div className="space-y-2">
          {BLOCK_TYPES.map((blockType) => (
            <Button
              key={blockType.id}
              variant="outline"
              size="sm"
              className="w-full justify-start text-left h-auto py-2"
              onClick={() => addBlock(blockType.id)}
            >
              <blockType.icon className="h-4 w-4 mr-2 flex-shrink-0" />
              <div>
                <div className="font-medium">{blockType.name}</div>
                <div className="text-xs text-muted-foreground">{blockType.description}</div>
              </div>
            </Button>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-border/50">
          <Button onClick={() => setShowPreview(!showPreview)} variant="outline" className="w-full mb-2">
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button onClick={handleSave} className="w-full">
            <ArrowRight className="h-4 w-4 mr-2" />
            Save & Continue
          </Button>
        </div>
      </div>

      {/* Center - Canvas (always visible — primary on mobile, center column on desktop) */}
      <div className={`${isMobile ? 'flex-1 pb-24' : 'flex-1'} overflow-y-auto bg-muted/20 p-3 md:p-8`}>
        <div className="max-w-[600px] mx-auto bg-white shadow-lg">
          {blocks.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Layout className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{isMobile ? 'Tap the + button to add blocks' : 'Click a block type to add it to your newsletter'}</p>
            </div>
          ) : (
            (() => {
              // First image / twoColumn block in the document is treated as the
              // "hero slot" — its empty placeholder gets a property-aware label
              // so users on a fresh template understand it'll be the hero.
              const heroSlotId = blocks.find(b => b.type === 'image' || b.type === 'twoColumn')?.id;
              return blocks.map((block, index) => (
                <div
                  key={block.id}
                  className={`relative group ${selectedBlockId === block.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => {
                    setSelectedBlockId(block.id);
                    if (isMobile && block.type !== 'image' && block.type !== 'twoColumn') {
                      setMobileSheet('settings');
                    }
                  }}
                >
                  <BlockPreview
                    block={block}
                    onUploadPlaceholderClick={openImageUploaderFor}
                    isHeroSlot={block.id === heroSlotId}
                  />
                  {/* Block controls — visible always on mobile (no hover), on hover on desktop */}
                  <div className={`absolute top-2 right-2 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity flex gap-1`}>
                    <Button size="icon" variant="secondary" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 'up'); }} disabled={index === 0}>
                      <MoveUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="secondary" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 'down'); }} disabled={index === blocks.length - 1}>
                      <MoveDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="destructive" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    {isMobile && (
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); setSelectedBlockId(block.id); setMobileSheet('settings'); }}
                        data-testid={`newsletter-block-edit-${block.id}`}
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ));
            })()
          )}
        </div>
      </div>

      {/* Right Panel - Block Settings — desktop sidebar OR mobile bottom sheet */}
      {(!isMobile || mobileSheet === 'settings') && (
      <div
        className={
          isMobile
            ? 'fixed inset-x-0 bottom-0 z-50 max-h-[75vh] flex flex-col rounded-t-2xl border-t border-border/50 bg-card shadow-2xl'
            : 'w-80 border-l border-border/50 bg-card p-4 overflow-y-auto'
        }
        data-testid="newsletter-settings-panel"
      >
        {isMobile && (
          <div className="flex items-center justify-between p-3 border-b border-border/50 sticky top-0 bg-card rounded-t-2xl">
            <div className="text-sm font-semibold">Block Settings</div>
            <button
              onClick={() => setMobileSheet(null)}
              className="p-2 -mr-2 rounded-md hover:bg-muted"
              aria-label="Close"
              data-testid="newsletter-sheet-close"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className={isMobile ? 'overflow-y-auto p-4' : ''}>
        {selectedBlock ? (
          <div className="space-y-4">
            <h3 className="font-semibold capitalize">{selectedBlock.type} Settings</h3>

            {/* Header settings */}
            {selectedBlock.type === 'header' && (
              <>
                <div>
                  <Label>Logo URL</Label>
                  <Input value={selectedBlock.logoUrl} onChange={(e) => updateBlock(selectedBlock.id, { logoUrl: e.target.value })} placeholder="https://..." />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={selectedBlock.title} onChange={(e) => updateBlock(selectedBlock.id, { title: e.target.value })} />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input value={selectedBlock.subtitle} onChange={(e) => updateBlock(selectedBlock.id, { subtitle: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Background</Label><Input type="color" value={selectedBlock.backgroundColor} onChange={(e) => updateBlock(selectedBlock.id, { backgroundColor: e.target.value })} /></div>
                  <div><Label>Text Color</Label><Input type="color" value={selectedBlock.textColor} onChange={(e) => updateBlock(selectedBlock.id, { textColor: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* Text settings */}
            {selectedBlock.type === 'text' && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Content</Label>
                    {/* Rewrite-with-AI — primarily useful for the long scraped
                        property description; lets users tighten it into a chosen
                        brand voice with one tap. */}
                    <RewriteWithAI
                      currentText={selectedBlock.content.replace(/<[^>]+>/g, '')}
                      onRewritten={(text) => updateBlock(selectedBlock.id, { content: `<p>${text}</p>` })}
                    />
                  </div>
                  <Textarea value={selectedBlock.content.replace(/<[^>]+>/g, '')} onChange={(e) => updateBlock(selectedBlock.id, { content: `<p>${e.target.value}</p>` })} rows={5} data-testid="newsletter-text-content" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Font Size</Label><Input type="number" value={selectedBlock.fontSize} onChange={(e) => updateBlock(selectedBlock.id, { fontSize: parseInt(e.target.value) })} /></div>
                  <div><Label>Text Color</Label><Input type="color" value={selectedBlock.textColor} onChange={(e) => updateBlock(selectedBlock.id, { textColor: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* Image settings */}
            {selectedBlock.type === 'image' && (
              <>
                <div>
                  <Label className="mb-2 block">Upload Image</Label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                    onClick={() => { const el = document.getElementById('newsletter-img-upload'); if(el) el.click(); }}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                      const file = e.dataTransfer.files[0];
                      if (!file || !file.type.startsWith('image/')) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        updateBlock(selectedBlock.id, { imageUrl: res.data.url });
                        toast.success('Image uploaded');
                      } catch { toast.error('Upload failed'); }
                    }}
                    data-testid="newsletter-image-dropzone"
                  >
                    {selectedBlock.imageUrl ? (
                      <img src={selectedBlock.imageUrl} alt="Preview" className="max-h-32 mx-auto rounded" />
                    ) : (
                      <>
                        <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Click or drag & drop</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                      </>
                    )}
                  </div>
                  <input
                    id="newsletter-img-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        updateBlock(selectedBlock.id, { imageUrl: res.data.url });
                        toast.success('Image uploaded');
                      } catch { toast.error('Upload failed'); }
                      e.target.value = '';
                    }}
                  />
                </div>
                <div>
                  <Label>Or paste URL</Label>
                  <Input value={selectedBlock.imageUrl} onChange={(e) => updateBlock(selectedBlock.id, { imageUrl: e.target.value })} placeholder="https://..." />
                </div>
                <div>
                  <Label>Caption</Label>
                  <Input value={selectedBlock.caption} onChange={(e) => updateBlock(selectedBlock.id, { caption: e.target.value })} />
                </div>
                {selectedBlock.imageUrl && (
                  <Button variant="outline" size="sm" className="w-full text-red-500 hover:text-red-600" onClick={() => updateBlock(selectedBlock.id, { imageUrl: '' })}>
                    Remove Image
                  </Button>
                )}
              </>
            )}

            {/* Two Column settings */}
            {selectedBlock.type === 'twoColumn' && (
              <>
                <div>
                  <Label>Layout</Label>
                  <Select value={selectedBlock.leftContent} onValueChange={(v) => updateBlock(selectedBlock.id, { leftContent: v, rightContent: v === 'image' ? 'text' : 'image' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Image | Text</SelectItem>
                      <SelectItem value="text">Text | Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Upload Image</Label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors"
                    onClick={() => { const el = document.getElementById('newsletter-twocol-upload'); if(el) el.click(); }}
                  >
                    {selectedBlock.imageUrl ? (
                      <img src={selectedBlock.imageUrl} alt="Preview" className="max-h-24 mx-auto rounded" />
                    ) : (
                      <p className="text-sm text-gray-400">Click to upload</p>
                    )}
                  </div>
                  <input
                    id="newsletter-twocol-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        updateBlock(selectedBlock.id, { imageUrl: res.data.url });
                        toast.success('Image uploaded');
                      } catch { toast.error('Upload failed'); }
                      e.target.value = '';
                    }}
                  />
                  <Input value={selectedBlock.imageUrl} onChange={(e) => updateBlock(selectedBlock.id, { imageUrl: e.target.value })} placeholder="Or paste URL..." className="mt-2" />
                </div>
                <div>
                  <Label>Text Content</Label>
                  <Textarea value={selectedBlock.textContent.replace(/<[^>]+>/g, '')} onChange={(e) => updateBlock(selectedBlock.id, { textContent: `<p>${e.target.value}</p>` })} rows={4} />
                </div>
              </>
            )}

            {/* Button settings */}
            {selectedBlock.type === 'button' && (
              <>
                <div>
                  <Label>Button Text</Label>
                  <Input value={selectedBlock.text} onChange={(e) => updateBlock(selectedBlock.id, { text: e.target.value })} />
                </div>
                <div>
                  <Label>Link URL</Label>
                  <Input value={selectedBlock.url} onChange={(e) => updateBlock(selectedBlock.id, { url: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Button Color</Label><Input type="color" value={selectedBlock.backgroundColor} onChange={(e) => updateBlock(selectedBlock.id, { backgroundColor: e.target.value })} /></div>
                  <div><Label>Text Color</Label><Input type="color" value={selectedBlock.textColor} onChange={(e) => updateBlock(selectedBlock.id, { textColor: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* Social Icons settings */}
            {selectedBlock.type === 'socialIcons' && (
              <>
                <div><Label>Facebook URL</Label><Input value={selectedBlock.facebook} onChange={(e) => updateBlock(selectedBlock.id, { facebook: e.target.value })} placeholder="https://facebook.com/..." /></div>
                <div><Label>Instagram URL</Label><Input value={selectedBlock.instagram} onChange={(e) => updateBlock(selectedBlock.id, { instagram: e.target.value })} placeholder="https://instagram.com/..." /></div>
                <div><Label>LinkedIn URL</Label><Input value={selectedBlock.linkedin} onChange={(e) => updateBlock(selectedBlock.id, { linkedin: e.target.value })} placeholder="https://linkedin.com/..." /></div>
                <div><Label>Twitter/X URL</Label><Input value={selectedBlock.twitter} onChange={(e) => updateBlock(selectedBlock.id, { twitter: e.target.value })} placeholder="https://twitter.com/..." /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Icon Size</Label><Input type="number" value={selectedBlock.iconSize} onChange={(e) => updateBlock(selectedBlock.id, { iconSize: parseInt(e.target.value) })} /></div>
                  <div><Label>Icon Color</Label><Input type="color" value={selectedBlock.iconColor} onChange={(e) => updateBlock(selectedBlock.id, { iconColor: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* Footer settings */}
            {selectedBlock.type === 'footer' && (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 mb-4">
                  <strong>CAN-SPAM Compliance:</strong> Include your business address and unsubscribe link.
                </div>
                <div><Label>Company Name</Label><Input value={selectedBlock.companyName} onChange={(e) => updateBlock(selectedBlock.id, { companyName: e.target.value })} /></div>
                <div><Label>Address</Label><Input value={selectedBlock.address} onChange={(e) => updateBlock(selectedBlock.id, { address: e.target.value })} placeholder="123 Main St, City, State ZIP" /></div>
                <div><Label>Phone</Label><Input value={selectedBlock.phone} onChange={(e) => updateBlock(selectedBlock.id, { phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={selectedBlock.email} onChange={(e) => updateBlock(selectedBlock.id, { email: e.target.value })} /></div>
                <div className="border-t pt-4 mt-4">
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" checked={selectedBlock.showSignature} onChange={(e) => updateBlock(selectedBlock.id, { showSignature: e.target.checked })} />
                    Show Signature
                  </Label>
                  {selectedBlock.showSignature && (
                    <>
                      <div className="mt-2"><Label>Your Name</Label><Input value={selectedBlock.signatureName} onChange={(e) => updateBlock(selectedBlock.id, { signatureName: e.target.value })} /></div>
                      <div className="mt-2"><Label>Your Title</Label><Input value={selectedBlock.signatureTitle} onChange={(e) => updateBlock(selectedBlock.id, { signatureTitle: e.target.value })} /></div>
                    </>
                  )}
                </div>
                <div className="border-t pt-4 mt-4">
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" checked={selectedBlock.showUnsubscribe} onChange={(e) => updateBlock(selectedBlock.id, { showUnsubscribe: e.target.checked })} />
                    Show Unsubscribe Link (Required)
                  </Label>
                  {selectedBlock.showUnsubscribe && (
                    <div className="mt-2"><Label>Unsubscribe Text</Label><Input value={selectedBlock.unsubscribeText} onChange={(e) => updateBlock(selectedBlock.id, { unsubscribeText: e.target.value })} /></div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div><Label>Background</Label><Input type="color" value={selectedBlock.backgroundColor} onChange={(e) => updateBlock(selectedBlock.id, { backgroundColor: e.target.value })} /></div>
                  <div><Label>Text Color</Label><Input type="color" value={selectedBlock.textColor} onChange={(e) => updateBlock(selectedBlock.id, { textColor: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* Divider settings */}
            {selectedBlock.type === 'divider' && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Color</Label><Input type="color" value={selectedBlock.color} onChange={(e) => updateBlock(selectedBlock.id, { color: e.target.value })} /></div>
                <div><Label>Thickness</Label><Input type="number" value={selectedBlock.thickness} onChange={(e) => updateBlock(selectedBlock.id, { thickness: parseInt(e.target.value) })} /></div>
              </div>
            )}

            {/* Spacer settings */}
            {selectedBlock.type === 'spacer' && (
              <div><Label>Height (px)</Label><Input type="number" value={selectedBlock.height} onChange={(e) => updateBlock(selectedBlock.id, { height: parseInt(e.target.value) })} /></div>
            )}

            {/* HTML Code settings */}
            {selectedBlock.type === 'html' && (
              <>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-2">
                  Paste any valid HTML. Inline styles recommended for email compatibility.
                </div>
                <div>
                  <Label>HTML Code</Label>
                  <Textarea
                    value={selectedBlock.code}
                    onChange={(e) => updateBlock(selectedBlock.id, { code: e.target.value })}
                    rows={12}
                    className="font-mono text-xs"
                    placeholder="<div>Your HTML here</div>"
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <p>Select a block to edit its settings</p>
          </div>
        )}
        </div>{/* /inner content wrapper for mobile sheet padding */}
      </div>
      )}{/* /right panel — mobile sheet gating */}

      {/* Mobile floating "+" FAB → opens block-picker sheet */}
      {isMobile && (
        <button
          type="button"
          onClick={() => setMobileSheet('add')}
          className="fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Add block"
          data-testid="newsletter-add-fab"
        >
          <span className="text-3xl leading-none">+</span>
        </button>
      )}

      {/* Mobile bottom sheet — Block-type picker */}
      {isMobile && mobileSheet === 'add' && (
        <div className="fixed inset-0 z-50 flex flex-col" data-testid="newsletter-add-sheet">
          <div className="flex-1 bg-black/40" onClick={() => setMobileSheet(null)} />
          <div className="bg-card rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-border/50 sticky top-0 bg-card rounded-t-2xl">
              <div className="text-sm font-semibold">Add Block</div>
              <button
                onClick={() => setMobileSheet(null)}
                className="p-2 -mr-2 rounded-md hover:bg-muted"
                aria-label="Close"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {BLOCK_TYPES.map((blockType) => (
                <button
                  key={blockType.id}
                  type="button"
                  className="w-full flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted active:bg-muted/70 text-left"
                  onClick={() => { addBlock(blockType.id); setMobileSheet(null); }}
                  data-testid={`newsletter-add-block-${blockType.id}`}
                >
                  <blockType.icon className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
                  <div>
                    <div className="font-medium text-sm">{blockType.name}</div>
                    <div className="text-xs text-muted-foreground">{blockType.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
